<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { dataManager, type Questionnaire } from '@/dataManager'
import VueFeather from 'vue-feather';

const emit = defineEmits<{
  (e: 'backButton', backUrl: string|null): void
  (e: 'userChanged'): void
}>()

const props = defineProps<{
  id: number
}>()

const router = useRouter()
const dm = dataManager.getInstance()
const questionnaire = dm.getQuestionnaire(props.id);

const currentQuestion = ref(0)

const answerTexts = [
    "NOT_USED",
    "Totalmente en desacuerdo",
    "Normalmente no",
    "Ni en acuerdo ni en desacuerdo",
    "Normalmente sí",
    "Totalmente de acuerdo"
];

function answerText(answerIndex: number|null): string {
  if (answerIndex == null) {
    return ''
  }
  return answerTexts[answerIndex]
}
const autoAdvance = true

type Answer = {
  answer: number|null
}
const questionnaireAnswers = ref<Answer[]>(defaultAnswers(questionnaire))

function defaultAnswers(questionnaire: Questionnaire): Answer[] {
  let myDefaultAnswers: Answer[] = [];

  for (let i = 0; i < questionnaire.questions.length; i++) {
    myDefaultAnswers.push({answer:  null})
  }
  return myDefaultAnswers;
}

onMounted(() => {
  emit('backButton', '/questionnaires');
  (document.querySelector("dialog.instructions") as HTMLDialogElement).showModal();
})

const warningText = ref('')

function selectOption(n: number): void {
  if (questionnaireAnswers.value === undefined) {
    throw Error('Default values not set yet')
  }
  questionnaireAnswers.value[currentQuestion.value].answer = n;

  // warning popup, if needed
  for (let i=0; i < questionnaire.results.length; i++) {
    let result = questionnaire.results[i]
    if (result.condition.startsWith(`P${currentQuestion.value + 1}`)) {
      let threshold = Number.parseInt(result.condition.split(" ")[2])
      if (n > threshold) {
        warningText.value = result.text;
        (document.querySelector("dialog.danger-alert") as HTMLDialogElement).showModal();
      }
    }
  }

  if (autoAdvance) {
    if (isLastQuestion()) {
      finish()
    } else {
      currentQuestion.value = currentQuestion.value + 1
    }
  }
}

function finish() {
  if (! allQuestionsAnswered()) {
    (document.querySelector("dialog.finish-warning") as HTMLDialogElement).showModal()
    return
  }

  dm.saveQuestionnaireResults(props.id, questionnaireAnswers.value as {answer: number}[])
  emit('userChanged')
  router.push(`/questionnaire/${props.id}/results`)
}

function allQuestionsAnswered(): boolean {
  for(let i = 0; i < questionnaire.questions.length; i ++) {
    if (questionnaireAnswers.value[i].answer === null) {
      return false
    }
  }
  return true
}

function closeDialog(event: Event) {
  if (event.target != null) {
    let dialog = (event.target as HTMLButtonElement).closest("dialog");
    if (dialog) {
      dialog.close();
    }
  }
}

function closeFinishWarningDialog(event: Event): void {
  closeDialog(event);
  for(let i = 0; i < questionnaire.questions.length; i ++) {
    if (questionnaireAnswers.value[i].answer === null) {
      currentQuestion.value = i;
      break;
    }
  }
}

function isLastQuestion(): boolean {
  return currentQuestion.value === questionnaire.questions.length - 1
}
</script>

<template>
<div class="content-wrapper">
  <h3 class="questionary-name">{{ questionnaire.title }}</h3>

  <div class="question-card-container">
  <div v-for="(question, index) in questionnaire.questions"
    class="question-card"
    :class="{current: index == currentQuestion,
      prev: index < currentQuestion,
      next: index > currentQuestion}"
    >
    <p class="question">{{ question.body }}</p>

    <div class="image-wrapper">
      <div class="image-aspect-wrapper">
        <img class="main-image" :src="'/images/' + question.image" />
      </div>
    </div>

    <div class="emotions">
        <img src="@/assets/thumbs-down.png" />
        <p class="legend">{{ answerText(questionnaireAnswers[index].answer) }}</p>
        <img src="@/assets/thumbs-up.png" />
    </div>
    <div class="labels">
        <span>Totalmente en desacuerdo</span>
        <span>Totalmente de acuerdo</span>
    </div>
    <div class="answer-options">
        <button v-for="n in 5" :key="n"
          @click="selectOption(n)"
          :class="{ selected: questionnaireAnswers[index].answer == n }"
        >{{ n }}</button>
    </div>
  </div>
  </div>

  <div class="navigation">
      <button :disabled="currentQuestion <= 0" @click="currentQuestion --">
        <VueFeather class="back-button-arrow" type="chevron-left"/>
      </button>
      <p class="pagination"><span class="currentQuestionIndex">{{ currentQuestion + 1 }}</span> / <span class="totalQuestions">{{ questionnaire.questions.length }}</span></p>
      <button v-if="! isLastQuestion()" @click="currentQuestion ++">
          <VueFeather class="back-button-arrow" type="chevron-right"/>
      </button>
      <button v-if="isLastQuestion()" @click="finish()"
        :class="allQuestionsAnswered()? '' : 'disabled'"
      >
        Finalizar
      <!--
        <VueFeather class="back-button-arrow" type="chevron-right"/>
        <VueFeather class="back-button-arrow" type="disc"/>
      -->
      </button>
  </div>

  <dialog class="instructions" closedby="any">
    <h3>Instrucciones</h3>
    <p v-if="dm.isTutoredMinor()">A continuación verás una serie de
      afirmaciones. Debes indicar cuánto
      describen lo que <b>tu hijo o hija</b> ha estado viviendo o mostrando,
      especialmente durante las <b>últimas dos semanas.</b> Marca el número que mejor
      refleje su situación: <b>1</b> significa que <b>no le ocurre en absoluto</b> (no estás
      de acuerdo con la afirmación respecto a tu hijo/a) y <b>5</b> que <b>le ocurre muy
      a menudo</b> (estás totalmente de acuerdo con la afirmación respecto a tu
      hijo/a).</p>
    <p v-if="dm.isAdult()">A continuación verás una serie de
      afirmaciones. Debes indicar cuánto describen lo que estás viviendo,
      pensando sobre todo en las <b>últimas dos semanas</b>. Marca el número que mejor
      refleje tu situación: <b>1</b> significa que <b>no te ocurre en absoluto</b> (no estás
      de acuerdo con la afirmación) y <b>5</b> que te ocurre <b>muy a menudo</b> (estás
      totalmente de acuerdo con la afirmación).</p>
    <p v-for="n in 5" :key="n" class="sample">
      <button>{{ n }}</button> <span>{{ answerTexts[n] }}</span>
    </p>

    <div class="buttons"><button @click="closeDialog($event)">Ok</button></div>
  </dialog>

  <dialog class="danger-alert" closedby="any">
    <h3>Atención</h3>
    <p v-html="warningText"></p>
    <div class="buttons"><button @click="closeDialog($event)">Ok</button></div>
  </dialog>

  <dialog class="finish-warning">
    <h3>Atención</h3>
    <p>Debes contestar a todas las cuestiones antes de finalizar el cuestionario.</p>
    <div class="buttons"><button @click="closeFinishWarningDialog($event)">Ok</button></div>
  </dialog>
</div>
</template>

<style scoped>
.content-wrapper {
  width: 500px;
  max-width: 100%;

  height: 100%;
  background-color: var(--color-background);
  flex-grow: 1;

  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 5px;

  overflow-x: visible;
}

.question-card-container {
  position: relative;
  height: 100%;
  width: 100%;
}

.question-card {
  position: absolute;
  top: 0;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  transition: transform 0.5s ease;

  &.prev {
    transform: translateX(calc(-50vw - 50%));
  }

  &.next {
    transform: translateX(calc(50vw + 50%));
  }
}

h3 {
    font-style: italic;
    margin-top: 0;
    margin-bottom: 10px;
}

.content-wrapper h3, .content-wrapper div, .content-wrapper p {
    flex-grow: 0;
    max-width: 500px;
}

.content-wrapper div.image-wrapper {
    flex-grow: 1;
    width: 100%;
    height: 20px;
    box-sizing: border-box;
    text-align: center;

    display: flex;
    flex-direction: column;
    justify-content: center;
    .image-aspect-wrapper {
      max-height: 100%;
      aspect-ratio: 4 / 3;
    }
}

.content-wrapper img.main-image {
    width: 100%;
    height: 100%;

    object-fit: cover;
    aspect-ratio: 1.33;
    display: inline-block;

    border-radius: 20px;
    box-shadow: 2px 1px 1px 1px rgba(0, 0, 0, 0.24);

    /* center vertically, but without resizing */
    position: relative;
    top: 50%;
    transform: translateY(-50%);
}

.question {
    /* font-weight: bold; */
    text-align: center;
    margin-left: 8px;
    margin-right: 8px;
    margin-top: 0;
    color: black;
}

.answer-options {
    margin: 20px 0;
    margin-bottom: 0;
    display: flex;
    justify-content: space-between;
    width: 100%;
    max-width: 572px;
}

.answer-options button, .instructions .sample button {
    min-width: 1px;
    aspect-ratio: 1.3;

    border: 2px solid #5bc6d7;
    border-radius: 50px;
    padding: 0;
    margin: 1%;

    background-color: white;
    color: #5bc6d7;
    margin-top: -30px;
}

.instructions .sample button {
  display: inline-flex;
  margin-top: 0;
}

.answer-options button.selected {
    background-color: #ff6800;
    border: 2px solid #ff6800;
    color: white;
}
.emotions {
    margin: 20px 0;
    display: flex;
    max-width: 572px;
    width: 100%;
    justify-content: space-between;
    align-items: center;
}
.emotions img {
    border: 0;
    height: 40px;
    padding-left: 10px;
    padding-right: 10px;
}
.emotions:last-child {
  text-align: right;
}

.legend {
    margin: 0;
    min-height: 25px;
    text-align: center;
}
.navigation svg {
    aspect-ratio: 1;
    height: 32px;
    color: white;
    margin: 0;
}
.navigation {
    display: flex;
    align-items: center;
}
.navigation button {
    border: 0;
    background-color: var(--main-color);
    color: white;
    width: auto;

    margin: 10px;
}

.navigation button:disabled {
  box-shadow: 0 0;
  filter: brightness(0.8);
}
.navigation button:disabled:hover {
  cursor: default;
}
.navigation button:disabled:active {
  transform: scale(1);
}

.navigation .pagination {
  min-width: 80px;
  text-align: center;
}

.labels {
    font-size: 8pt;
    color: black;
    display: flex;
    justify-content: space-between;
    max-width: 572px;
    width: 100%;

    position: relative;
    top: -11px;
    padding-left: 5px;
    padding-right: 5px;
    box-sizing: border-box;
}

button i {
  padding: 0;
}

button.disabled {
  background-color: lightgray;
}
</style>
